import React, { useEffect, useState } from 'react';
import api from './api';
import { projectCode, formatDate, taskCode, formatMontant, ESCALADE_SERVICE_JOURS, ESCALADE_DIRECTION_JOURS, userDisplayName } from './helpers';
import ProgressChart from './ProgressChart';
import ProjectCard from './ProjectCard';

// Le tableau de bord est une synthèse : il ne montre que les projets qui
// demandent de l'attention. Le portefeuille complet a sa propre page.
const MAX_PROJETS_DASHBOARD = 12;

function Dashboard({ onSelectProject, onVoirPortefeuille, users = [], searchQuery }) {
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [echeances, setEcheances] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [detailRetard, setDetailRetard] = useState(false);
  const [blocages, setBlocages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    Promise.all([
      api.get('/reports/dashboard'),
      api.get('/projects'),
      api.get('/deadlines'),
      api.get('/progress-history'),
      api.get('/blocages'),
    ])
      .then(([statsRes, projectsRes, echRes, histRes, blocRes]) => {
        if (!mounted) return;
        setStats(statsRes.data);
        setProjects(projectsRes.data);
        setEcheances(echRes.data);
        setHistorique(histRes.data);
        setBlocages(blocRes.data?.blocages || []);
      })
      .catch(() => {
        if (mounted) setError('Impossible de charger les données du dashboard.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, []);

  if (loading) return <p>Chargement...</p>;
  if (error) return <div className="error-message">{error}</div>;

  const query = (searchQuery || '').trim().toLowerCase();

  const enRetard = echeances?.overdue || [];
  const aujourdhui = echeances?.today || [];
  const bientot = echeances?.soon || [];

  // Tâches en retard par projet : c'est le premier signal à faire remonter
  // sur une carte.
  const retardsParProjet = {};
  enRetard.forEach((t) => {
    retardsParProjet[t.project_id] = (retardsParProjet[t.project_id] || 0) + 1;
  });

  // Ordre d'attention : projets en retard, puis bloqués, puis avec des tâches
  // en retard, puis actifs par échéance la plus proche. Les projets terminés
  // ou annulés n'ont rien à faire sur un tableau de bord.
  const score = (p) =>
    (p.status === 'Delayed' ? 1000 : 0) +
    (p.blocked_count > 0 ? 500 : 0) +
    (retardsParProjet[p.id] || 0) * 10;

  const projetsAttention = projects
    .filter((p) => p.status !== 'Completed' && p.status !== 'Cancelled' && !p.archived)
    .sort((a, b) => score(b) - score(a) || String(a.end_date || '9').localeCompare(String(b.end_date || '9')));

  const visibleProjects = query
    ? projects.filter((p) => p.name.toLowerCase().includes(query) || projectCode(p.id).toLowerCase().includes(query) || (p.description || '').toLowerCase().includes(query))
    : projetsAttention.slice(0, MAX_PROJETS_DASHBOARD);

  const nbMasques = query ? 0 : projetsAttention.length - visibleProjects.length;
  const nomPersonne = (id) => userDisplayName(users.find((u) => u.id === id));

  return (
    <div className="fade-in">
      {(enRetard.length > 0 || aujourdhui.length > 0) && (
        <div className="alerte-retard" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              {enRetard.length > 0 && (
                <>
                  <strong>{enRetard.length} tâche{enRetard.length > 1 ? 's' : ''} en retard</strong>
                  {aujourdhui.length > 0 && ' · '}
                </>
              )}
              {aujourdhui.length > 0 && (
                <strong>{aujourdhui.length} à échéance aujourd'hui</strong>
              )}
              {bientot.length > 0 && (
                <span style={{ color: 'var(--text-secondary)' }}> · {bientot.length} dans les 7 jours</span>
              )}
            </div>
            <button className="btn btn-sm" onClick={() => setDetailRetard(!detailRetard)}>
              {detailRetard ? 'Masquer le détail' : 'Voir le détail'}
            </button>
          </div>

          {detailRetard && (
            <div style={{ marginTop: 12, borderTop: '1px solid rgba(217,83,79,0.25)', paddingTop: 10 }}>
              {[...enRetard, ...aujourdhui].slice(0, 15).map((t) => (
                <div
                  key={t.id}
                  style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '5px 0', flexWrap: 'wrap' }}
                >
                  <span style={{ color: 'var(--text-muted)', minWidth: 66 }}>{taskCode(t.id)}</span>
                  <span style={{ flex: 1, minWidth: 180 }}>{t.name}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{nomPersonne(t.assigned_to)}</span>
                  <span style={{ fontWeight: 600 }}>{formatDate(t.due_date)}</span>
                </div>
              ))}
              {enRetard.length + aujourdhui.length > 15 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  … et {enRetard.length + aujourdhui.length - 15} autres.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-dot" style={{ background: '#517997' }} />
          <div className="stat-label">Projets actifs</div>
          <div className="stat-value">{stats.activeProjects}</div>
        </div>
        <div className="stat-card">
          <span className="stat-dot" style={{ background: '#D9534F' }} />
          <div className="stat-label">Projets en retard</div>
          <div className="stat-value">{stats.delayedProjects}</div>
        </div>
        <div className="stat-card">
          <span className="stat-dot" style={{ background: '#4CAF50' }} />
          <div className="stat-label">Projets terminés</div>
          <div className="stat-value">{stats.completedProjects}</div>
        </div>
        <div className="stat-card">
          <span className="stat-dot" style={{ background: '#8CA0B3' }} />
          <div className="stat-label" title="Part des tâches terminées, tous projets confondus. Ce n'est pas l'avancement physique des installations.">Tâches terminées (portefeuille)</div>
          <div className="stat-value">{stats.overallTaskProgress}%</div>
          <div className="stat-sub">{stats.doneTasks} sur {stats.totalTasks} tâches</div>
        </div>
        <div className="stat-card">
          <span className="stat-dot" style={{ background: '#C19E54' }} />
          <div className="stat-label">Budget consommé</div>
          {stats.totalBudget > 0 ? (
            <>
              <div className="stat-value">{stats.budgetUsedPercent}%</div>
              <div className="stat-sub">{formatMontant(stats.totalSpent)} dépensés sur {formatMontant(stats.totalBudget)}</div>
              {stats.totalEngaged > 0 && (
                <div className="stat-sub">{formatMontant(stats.totalEngaged)} engagés ({stats.budgetEngagedPercent || 0}%)</div>
              )}
            </>
          ) : (
            <>
              <div className="stat-value"><span className="valeur-absente">Non renseigné</span></div>
              <div className="stat-sub">Aucune enveloppe saisie dans les projets.</div>
            </>
          )}
        </div>
      </div>

      {blocages.length > 0 && (
        <section className="blocages-panneau">
          <div className="blocages-entete">
            <h2 style={{ margin: 0 }}>
              Blocages en cours
              <span className="blocages-total">{blocages.length}</span>
            </h2>
            <span className="blocages-legende">
              Remontée automatique à {ESCALADE_SERVICE_JOURS} jours (chef de service),
              puis à {ESCALADE_DIRECTION_JOURS} jours (direction).
            </span>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Jours</th>
                  <th>Tâche</th>
                  <th>Projet</th>
                  <th>On attend</th>
                  <th>De qui</th>
                  <th>Responsable</th>
                </tr>
              </thead>
              <tbody>
                {blocages.map((b) => (
                  <tr
                    key={b.task_id}
                    className="clickable"
                    onClick={() => {
                      const projet = projects.find((p) => p.id === b.project_id);
                      if (projet) onSelectProject(projet);
                    }}
                  >
                    <td>
                      <span className={`blocage-pastille blocage-pastille-${b.escalade || 'normal'}`}>
                        {b.jours} j
                      </span>
                    </td>
                    <td>
                      <span style={{ color: 'var(--text-muted)', marginRight: 8 }}>{taskCode(b.task_id)}</span>
                      {b.name}
                    </td>
                    <td>{b.project_name}</td>
                    <td style={{ maxWidth: 280 }}>{b.blocage_motif}</td>
                    <td>
                      {b.blocage_qui}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{b.blocage_origine_label}</div>
                    </td>
                    <td>{b.assigned_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div style={{ marginBottom: 24 }}>
        <ProgressChart points={historique} titre="Avancement des projets dans le temps" />
      </div>

      <div className="dashboard-lien-portefeuille">
        <h2 style={{ margin: 0 }}>
          {query ? `Résultats pour « ${searchQuery} »` : 'Projets à suivre'}
        </h2>
        {!query && onVoirPortefeuille && (
          <button type="button" className="lien-discret" onClick={onVoirPortefeuille}>
            Voir le portefeuille complet ({projects.length} projets)
          </button>
        )}
      </div>
      {!query && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px' }}>
          Projets en retard, bloqués ou avec des tâches en retard en premier, puis par échéance la plus proche.
        </p>
      )}
      <div className="projects-grid">
        {visibleProjects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            users={users}
            overdueCount={retardsParProjet[project.id] || 0}
            onSelect={onSelectProject}
          />
        ))}
        {visibleProjects.length === 0 && (
          <div className="empty-state">
            {query ? 'Aucun projet ne correspond à la recherche.' : 'Aucun projet en cours — tout est terminé ou archivé.'}
          </div>
        )}
      </div>
      {nbMasques > 0 && onVoirPortefeuille && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button type="button" className="btn" onClick={onVoirPortefeuille}>
            Voir les {nbMasques} autres projets en cours
          </button>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
